import React, { Suspense } from 'react'

import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom'

import { Loading } from '@elgatoylacaja/components'
import { compose } from 'lodash/fp'
import { AppContext, AppContextData } from './contexts/AppContext'

const LazyDemoRoutes = React.lazy(() => import('./DemoRoutes'))

const Terms = React.lazy(() => import('./views/Terms'))
const Intro = React.lazy(() => import('./views/Intro'))
const PrimeraFase = React.lazy(() => import('./views/PrimeraFase'))
const Regressors = React.lazy(() => import('./views/Regressors'))
const Thanks = React.lazy(() => import('./views/Thanks'))

type WrappedComponentType =
  | React.ComponentType<RouteComponentProps<any>>
  | React.ComponentType<any>

const condition = (appState: AppContextData) => (
  f: (x: AppContextData) => boolean
) => (r: string) => (WrappedComponent: WrappedComponentType) => (
  p: RouteComponentProps
) => {
  return p.match.isExact ? (
    f(appState) ? (
      <WrappedComponent {...p} />
    ) : (
      <Redirect to={r} {...p} />
    )
  ) : null
}

type LinkedRouteConfig = { prev?: string; route: string; next?: string }

class LinkedRoutes {
  paths: Array<[string, React.ComponentType<any>]>

  constructor(paths: Array<[string, React.ComponentType<any>]>) {
    this.paths = paths
  }

  config = () =>
    this.paths.map(([path, component], index, list): [
      LinkedRouteConfig,
      React.ComponentType
    ] => [
      {
        prev: index === 0 ? undefined : list[index - 1][0],
        route: path,
        next: index === list.length - 1 ? undefined : list[index + 1][0],
      },
      component,
    ])
}

const ExperimentRoutes = new LinkedRoutes([
  ['/terms', Terms],
  ['/intro', Intro],
  ['/primera-fase', PrimeraFase],
  ['/regressors', Regressors],
  ['/thanks', Thanks],
]).config()

const LazySwitchRoutes = () => {
  const { appState } = React.useContext(AppContext)

  const stateCondition = condition(appState)

  const alwaysTrue = stateCondition(_ => true)('')

  const visited = (r: string) =>
    stateCondition(appState => appState.visited.includes(r))(r)

  const notVisited = (r: string) =>
    stateCondition(appState => !appState.visited.includes(r))

  const requirePrevious = ({ prev, route, next }: LinkedRouteConfig) =>
    compose(
      prev !== undefined ? visited(prev) : alwaysTrue,
      next !== undefined ? notVisited(route)(next) : alwaysTrue
    )

  return (
    <Switch>
      <Redirect exact from="/" to="/terms" />
      <Suspense fallback={<Loading loading fixed />}>
        {ExperimentRoutes.map(([config, component]) => (
          <Route
            key={config.route}
            path={config.route}
            exact
            render={requirePrevious(config)(component)}
          />
        ))}

        {process.env.REACT_APP_ENVIRONMENT !== 'production' ? (
          <LazyDemoRoutes appState={appState} />
        ) : null}
      </Suspense>
    </Switch>
  )
}

export default LazySwitchRoutes
